import Popup from "../../lib/Popup";

import * as PIXI from "pixi.js";
import gsap from "gsap";
import { IGameSnacks } from "../../lib/GameSnacks";
import ServiceLocator from "../../ServiceLocator";
import Utils from "../../Utils";
import Button from "../../lib/Button";
import { Profile } from "../../lib/game_snacks/IUser";
import Session, { game_type } from "../../Session";
import { LiveEventData, LiveEventDataType, LivePlayerDataEvent, LivePresenceAction } from "../../lib/game_snacks/ILiveMultiplayer";
import { Signal } from "signals";
import PRNG from "../../lib/PRNG";
import { IStartGamePayload } from "./MatchMakingPopup";
import PlayerSlot from "./PlayerSlot";
import { game_event_type } from "../../service/MultiplayerEventManager";
import Settings from "../Settings";
import TutorialPopup from "./TutorialPopup";
import MatchmakingReadyAnim from "./MatchmakingReadyAnim";

declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class GuestPrivateRoomPopup extends Popup
{
    // #region Attributes //

    // private:

    private _url: string = null;

    private _matchmakingReadyAnim: MatchmakingReadyAnim = null;
    private _headerText: PIXI.Text = null;
    private _magnifyingGlass: PIXI.Sprite = null;
    private _arrPlayerSlot: Array<PlayerSlot> = null;
    private _settings: Settings = null;
    private _backBtn: Button = null;

    private _magnifyingGlassTween: gsap.core.Tween = null;

    private _onGameStarted: Signal = null;

    // #endregion //


    public set url( value: string ) { this._url = value; }

    public get onGameStarted(): Signal { return this._onGameStarted; } 


    // #region Methods //

    // public:

    public onEnter(): void
    {
        super.onEnter();        

        console.assert( this._url !== null, "GuestPrivateRoomPopup.ts :: onEnter() :: this._url cannot be null." );

        this._onGameStarted = new Signal();

        this._arrPlayerSlot = [];

        let bg: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.bg.texture );
        bg.anchor.set( 0.5 );
        bg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._root.addChild( bg );

        this._matchmakingReadyAnim = new MatchmakingReadyAnim();
        this._matchmakingReadyAnim.playerSlots = this._arrPlayerSlot;
        this._matchmakingReadyAnim.init();
        this._root.addChild( this._matchmakingReadyAnim );
        this._matchmakingReadyAnim.visible = false;

        this._headerText = new PIXI.Text( "Waiting for host\nto start the game...", { fontFamily: "asap_bold", fontSize: 50, fill: 0xffffdf, align: "center" } );
        this._headerText.anchor.set( 0.5 );
        this._headerText.position.set( ServiceLocator.app.width * 0.5, 218 - 20 );
        this._root.addChild( this._headerText );

        const kArrPos: Array<PIXI.Point> = [ 
            new PIXI.Point( ServiceLocator.app.width * 0.5, 764 ),
            new PIXI.Point( 105, 550 ),
            new PIXI.Point( 526, 550 ),
            new PIXI.Point( ServiceLocator.app.width * 0.5, 354 ) ];
        for ( let i: number = 0; i < Session.i.kGameRoomSize; ++i )
        {
            let playerSlot: PlayerSlot = new PlayerSlot();
            playerSlot.position.copyFrom( kArrPos[ i ] );
            this._root.addChild( playerSlot );            
            //
            this._arrPlayerSlot.push( playerSlot );
        }
        this._arrPlayerSlot[ 0 ].setPlayer( Session.i.profile );
        this._arrPlayerSlot[ 0 ].scale.set( 1.2 );
        this._updateOtherPlayerSlots();

        this._backBtn = new Button( { on: "button_back", pressed: "button_back_pressed" } );
        this._backBtn.anchor.set( 0.5 );
        this._backBtn.position.set( 58 );
        this._root.addChild( this._backBtn );
        this._backBtn.on( "pointertap", () => { 
            this._root.interactiveChildren = false;
            if ( window.GameSnacks )
            {
                window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.LEAVE )
                    .then( () => {
                        this._onClosed.dispatch( this, null ); 
                    } );
            }
            else
            {
                this._onClosed.dispatch( this, null ); 
            }
        } );

        this._magnifyingGlass = new PIXI.Sprite( ServiceLocator.resources.magnifying_glass.texture );
        this._magnifyingGlass.visible = Session.i.room.length < Session.i.kGameRoomSize;
        this._magnifyingGlass.anchor.set( 70/173, 68/206 );
        this._magnifyingGlass.position.set( ServiceLocator.app.width * 0.5 - 40, 577 );
        this._root.addChild( this._magnifyingGlass );
        //
        this._magnifyingGlassTween = gsap.to( {}, { duration: 2, repeat: -1, onUpdate: () => {
            let m: PIXI.Matrix = PIXI.Matrix.IDENTITY.clone();
            m.tx = -40;
            m.rotate( this._magnifyingGlassTween.progress() * Math.PI * 2 );
            
            this._magnifyingGlass.position.set( ServiceLocator.app.width * 0.5 + m.tx, 577 + m.ty );
        } } );

        this._settings = new Settings();
        this._settings.init( false );
        this._settings.position.set( ServiceLocator.app.width - 58, 58 );
        this._root.addChild( this._settings );
        this._settings.onTutorial.add( () => { 
            let tutorialPopup: TutorialPopup = new TutorialPopup();
            tutorialPopup.init();
            this._root.addChild( tutorialPopup );
            tutorialPopup.onClosed.add( ( popup: TutorialPopup ) => {
                popup.end();
            } );
        } );
        //
        const kSettingsScale: number = 100 / this._settings.width;
        this._settings.position.x -= 58 * ( kSettingsScale - 1 ); 
        this._settings.position.y += 58 * ( kSettingsScale - 1 ) - 4; 
        this._settings.scale.set( kSettingsScale );

        ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.add( this._onMultiplayer_PlayerPresenceUpdated, this );
        ServiceLocator.multiplayerEventManager.onEventsReceived.add( this._onMultiplayer_EventsReceived, this );
        ServiceLocator.multiplayerEventManager.onProfileChange.add( this._onProfile_Changed, this );

        window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.JOINED_PRIVATE_ROOM, uid: Session.i.profile.uid } ) } );
    }

        private _onMultiplayer_PlayerPresenceUpdated( presenceEventPromise: Promise<LivePlayerDataEvent> ): void
        {
            presenceEventPromise
                .then( ( livePlayerDataEvent: LivePlayerDataEvent ) => {
                    if ( livePlayerDataEvent.action == LivePresenceAction.JOIN )
                    {
                        let profile: Profile = livePlayerDataEvent.profile;
                        profile.uid = livePlayerDataEvent.uid;
                        let isRepeated: boolean = false;
                        for ( let profileIt of Session.i.room )
                        {
                            if ( profileIt.uid == profile.uid )
                            {
                                isRepeated = true;
                                break;
                            }
                        }
                        if ( !isRepeated )
                        {
                            Session.i.room.push( livePlayerDataEvent.profile );
                        }

                        if ( profile.uid == Session.i.profile.uid )
                        {
                            this._arrPlayerSlot[ 0 ].setPlayer( profile );
                        }
                        this._updateOtherPlayerSlots();

                        this._magnifyingGlass.visible = Session.i.room.length < Session.i.kGameRoomSize;
                    }
                    else if ( livePlayerDataEvent.action == LivePresenceAction.LEAVE )
                    {
                        if ( Session.i.isGameStarted )
                        {
                            if ( Session.i.profile.uid != livePlayerDataEvent.uid ) 
                            {
                                for ( let i: number = 0; i < Session.i.room.length; ++i )
                                {
                                    if ( Session.i.room[ i ].uid == livePlayerDataEvent.uid )
                                    {
                                        Session.i.room[ i ].isBot = true;
                                        break;
                                    }
                                }
                            }
                        }
                        else
                        {
                            if ( Session.i.room[ 0 ].uid == livePlayerDataEvent.uid
                                && Session.i.room[ 1 ].uid == Session.i.profile.uid )
                            {
                                Session.i.room.splice( 0, 1 );

                                for ( let profile of Session.i.room )
                                {
                                    profile.isReady = true;
                                }

                                this._onClosed.dispatch( this, this._url ); 
                            }
                            else
                            {
                                if ( Session.i.profile.uid != livePlayerDataEvent.uid ) 
                                {
                                    for ( let i: number = 0; i < Session.i.room.length; ++i )
                                    {
                                        if ( Session.i.room[ i ].uid == livePlayerDataEvent.uid )
                                        {
                                            Session.i.room.splice( i, 1 );
                                            break;
                                        }
                                    }

                                    this._updateOtherPlayerSlots();
                                    this._magnifyingGlass.visible = true;
                                }
                            }
                        }
                    }
                } );
        }

            private _updateOtherPlayerSlots(): void
            {
                for ( let i: number = 1; i < Session.i.kGameRoomSize; ++i )
                {
                    this._arrPlayerSlot[ i ].empty();
                }

                let myProfileIndex: number = 0;
                while ( Session.i.room[ myProfileIndex ].uid != Session.i.profile.uid )
                {
                    myProfileIndex += 1;
                }
                
                switch ( myProfileIndex )
                {
                    case 1:
                        {
                            if ( Session.i.room.length == 2 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 0 ] );
                            }
                            else if ( Session.i.room.length == 3 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 2 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 0 ] );
                            }
                            else
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 3 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 0 ] );
                                this._arrPlayerSlot[ 3 ].setPlayer( Session.i.room[ 2 ] );
                            }
                            break;
                        }

                    case 2:
                        {
                            if ( Session.i.room.length < 4 )
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 0 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 1 ] );
                            }
                            else
                            {
                                this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 0 ] );
                                this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 3 ] );
                                this._arrPlayerSlot[ 3 ].setPlayer( Session.i.room[ 1 ] );
                            }
                            break;
                        }

                    case 3:
                        {
                            this._arrPlayerSlot[ 1 ].setPlayer( Session.i.room[ 2 ] );
                            this._arrPlayerSlot[ 2 ].setPlayer( Session.i.room[ 1 ] );
                            this._arrPlayerSlot[ 3 ].setPlayer( Session.i.room[ 0 ] );
                            break;
                        }
                }
            }

        private _onMultiplayer_EventsReceived( eventsPromise: Promise<LiveEventData[]> ): void
        {
            eventsPromise
                .then( ( arrData: LiveEventData[] ) => {
                    let isStartGame: boolean = false;
                    while ( arrData.length > 0 )
                    {
                        let data: LiveEventData = arrData.shift();
                        if ( !Session.i.isGameStarted && data.payload )
                        {
                            let startGamePayload: IStartGamePayload = JSON.parse( data.payload );
                            if ( data.type !== LiveEventDataType.START_GAME
                                && ( startGamePayload as any ).type !== game_event_type.PLAY_AGAIN_PRIVATE_GAME )
                            {
                                startGamePayload = null;
                            }

                            if ( startGamePayload )
                            {
                                Session.i.prng = new PRNG( startGamePayload.seed );
                                Session.i.room = startGamePayload.room;

                                isStartGame = true;
                            }
                        }
                        else
                        {
                            Session.i.gameEvents.push( data );
                        }
                    }

                    if ( isStartGame )
                    {
                        Session.i.isGameStarted = true;

                        if ( Session.i.room.length < Session.i.kGameRoomSize )
                        {
                            this._arrPlayerSlot[ 3 ].visible = false;
                            //this._arrPlayerSlot[ 2 ].position.copyFrom( this._arrPlayerSlot[ 3 ].position );*/
                        }

                        if ( this._magnifyingGlassTween )
                        {
                            this._magnifyingGlassTween.kill();
                            this._magnifyingGlassTween = null;
                        }
                        this._magnifyingGlass.visible = false;
                        //
                        this._headerText.visible = false;
                        this._backBtn.visible = false;
                        this._settings.visible = false;

                        this._matchmakingReadyAnim.visible = true;
                        let tl: gsap.core.Timeline = this._matchmakingReadyAnim.play();
                        tl.call( () => { this._onGameStarted.dispatch( this ); }, null );
                    }
                } );
        }

        private _onProfile_Changed( profile: Profile): void
        {
            Session.i.profile = profile;

            this._arrPlayerSlot[ 0 ].setPlayer( Session.i.profile )
        }

    public onLeave(): void
    {
        this._onGameStarted.dispose();
        this._onGameStarted = null;

        ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
        ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
        ServiceLocator.multiplayerEventManager.onProfileChange.remove( this._onProfile_Changed, this );

        Session.i.isGameStarted = false;

        if ( this._magnifyingGlassTween )
        {
            this._magnifyingGlassTween.kill();
            this._magnifyingGlassTween = null;
        }

        this._matchmakingReadyAnim.end();
        this._matchmakingReadyAnim = null;

        this._settings.end();
        this._settings = null;

        super.onLeave();
    }

    public onUpdate( dt: number ): void
    {
        if ( this._matchmakingReadyAnim.visible )
        {
            this._matchmakingReadyAnim.update( dt );
        }
    }

    // #endregion //
}